import { createContext, type PropsWithChildren, useCallback, useContext, useRef, useState } from 'react'

export type ZOOM_STATE = 'MIN' | 'MAX' | 'DEFAULT'

interface ZoomHandles {
  zoomIn: () => void
  zoomOut: () => void
  resetZoom: () => void
  isZoomed: () => boolean
  getZoomState: () => ZOOM_STATE
}

interface ZoomContextType {
  registerZoomHandles: (handles: ZoomHandles) => void
  zoomIn: () => void
  zoomOut: () => void
  resetZoom: () => void
  isZoomed: boolean
  notifyZoomChange: () => void
  zoomState: ZOOM_STATE
}

const ZoomContext = createContext<ZoomContextType | undefined>(undefined)

export function ZoomProvider({ children }: PropsWithChildren) {
  const zoomHandlesRef = useRef<ZoomHandles | null>(null)
  const [isZoomed, setIsZoomed] = useState(false)
  const [zoomState, setZoomState] = useState<ZOOM_STATE>('DEFAULT')

  const notifyZoomChange = useCallback(() => {
    const zoomed = zoomHandlesRef.current?.isZoomed() ?? false
    setIsZoomed(prev => prev === zoomed ? prev : zoomed)
    setZoomState(zoomHandlesRef.current?.getZoomState() ?? 'DEFAULT')
  }, [])

  const registerZoomHandles = useCallback((handles: ZoomHandles) => {
    zoomHandlesRef.current = handles
    notifyZoomChange()
  }, [notifyZoomChange])

  const zoomIn = useCallback(() => {
    zoomHandlesRef.current?.zoomIn()
    notifyZoomChange()
  }, [notifyZoomChange])

  const zoomOut = useCallback(() => {
    zoomHandlesRef.current?.zoomOut()
    notifyZoomChange()
  }, [notifyZoomChange])

  const resetZoom = useCallback(() => {
    zoomHandlesRef.current?.resetZoom()
    notifyZoomChange()
  }, [notifyZoomChange])

  const contextValue: ZoomContextType = {
    registerZoomHandles,
    zoomIn,
    zoomOut,
    resetZoom,
    isZoomed,
    notifyZoomChange,
    zoomState,
  }

  return (
    <ZoomContext.Provider value={contextValue}>
      {children}
    </ZoomContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useZoomContext() {
  const context = useContext(ZoomContext)

  if (context === undefined) {
    throw new Error('useZoom must be used within a ZoomProvider')
  }

  return context
}

